/**
 * 테이블 셀 병합/해제 유틸리티
 */
import * as fabric from 'fabric';
import { calcCellPosition, recalcTableLayout, NOOP_LAYOUT_MANAGER } from './tableUtils';
import { DEFAULT_TEXT_INSET } from '../constants/editorConstants';

/**
 * 선택된 셀들로부터 사각형 영역 계산
 * @param {Array<{row:number, col:number}>} cells
 * @returns {{minRow, maxRow, minCol, maxCol}}
 */
export const getCellBounds = (cells) => {
  if (!cells || cells.length === 0) return null;
  return {
    minRow: Math.min(...cells.map(c => c.row)),
    maxRow: Math.max(...cells.map(c => c.row)),
    minCol: Math.min(...cells.map(c => c.col)),
    maxCol: Math.max(...cells.map(c => c.col)),
  };
};

/**
 * 사각형 영역이 기존 병합 셀과 충돌하는지 검사
 * @param {fabric.Group} tableGroup
 * @param {{minRow, maxRow, minCol, maxCol}} bounds
 * @returns {boolean} 충돌 여부
 */
export const hasMergeConflict = (tableGroup, bounds) => {
  const children = tableGroup.getObjects();
  for (const child of children) {
    if (!child._isTableCell) continue;
    const gs = child._gridSpan || 1;
    const rs = child._rowSpan || 1;
    if (gs <= 1 && rs <= 1) continue;

    const cellEndRow = child._cellRow + rs - 1;
    const cellEndCol = child._cellCol + gs - 1;

    // 이 병합 셀이 선택 영역과 겹치는지 확인
    const overlaps = !(cellEndRow < bounds.minRow || child._cellRow > bounds.maxRow ||
                       cellEndCol < bounds.minCol || child._cellCol > bounds.maxCol);
    if (!overlaps) continue;

    // 완전히 포함되면 OK (전체 해제 후 재병합)
    const fullyContained = child._cellRow >= bounds.minRow && cellEndRow <= bounds.maxRow &&
                           child._cellCol >= bounds.minCol && cellEndCol <= bounds.maxCol;
    if (!fullyContained) return true; // 부분 겹침 = 충돌
  }
  return false;
};

/**
 * 셀 병합 실행
 * @param {fabric.Group} tableGroup - 테이블 그룹
 * @param {Array<{row:number, col:number}>} selectedCells - 선택된 셀 배열
 * @param {fabric.Canvas} canvas
 * @returns {boolean} 성공 여부
 */
export const mergeCells = (tableGroup, selectedCells, canvas) => {
  if (!tableGroup || selectedCells.length < 2) return false;

  const bounds = getCellBounds(selectedCells);
  if (!bounds) return false;

  const savedPos = { left: tableGroup.left, top: tableGroup.top };

  // 충돌 검사: 기존 병합 영역 내에서 먼저 해제
  const toUnmergeFirst = [];
  for (const child of tableGroup.getObjects()) {
    if (!child._isTableCell) continue;
    const gs = child._gridSpan || 1;
    const rs = child._rowSpan || 1;
    if (gs <= 1 && rs <= 1) continue;

    const cellEndRow = child._cellRow + rs - 1;
    const cellEndCol = child._cellCol + gs - 1;
    const overlaps = !(cellEndRow < bounds.minRow || child._cellRow > bounds.maxRow ||
                       cellEndCol < bounds.minCol || child._cellCol > bounds.maxCol);
    if (overlaps) {
      toUnmergeFirst.push({ row: child._cellRow, col: child._cellCol });
    }
  }

  // 기존 병합 해제
  for (const cell of toUnmergeFirst) {
    unmergeCells(tableGroup, cell.row, cell.col, canvas, true);
  }

  // ★ unmerge 후 자식 목록을 새로 가져옴 (stale reference 방지)
  const children = tableGroup.getObjects();

  const colWidths = tableGroup._tableColWidths;
  const rowHeights = tableGroup._tableRowHeights;
  const { minRow, maxRow, minCol, maxCol } = bounds;
  const gridSpan = maxCol - minCol + 1;
  const rowSpan = maxRow - minRow + 1;

  // 병합 영역의 텍스트 수집 (줄바꿈으로 연결)
  const texts = [];
  let firstTextChild = null;
  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minCol; c <= maxCol; c++) {
      const textChild = children.find(
        ch => ch._isTableCellText && ch._cellRow === r && ch._cellCol === c
      );
      if (textChild && textChild.text && textChild.text.trim()) {
        texts.push(textChild.text);
      }
      if (r === minRow && c === minCol) {
        firstTextChild = textChild;
      }
    }
  }

  // 좌상단 셀 외의 Rect+Textbox 제거
  const toRemove = [];
  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minCol; c <= maxCol; c++) {
      if (r === minRow && c === minCol) continue;
      const rect = children.find(
        ch => ch._isTableCell && ch._cellRow === r && ch._cellCol === c
      );
      const text = children.find(
        ch => ch._isTableCellText && ch._cellRow === r && ch._cellCol === c
      );
      if (rect) toRemove.push(rect);
      if (text) toRemove.push(text);
    }
  }
  // FitContentLayout 억제: remove()가 자식 위치를 오염시키는 것 방지
  const origLM = tableGroup.layoutManager;
  tableGroup.layoutManager = NOOP_LAYOUT_MANAGER;
  toRemove.forEach(obj => tableGroup.remove(obj));
  tableGroup.layoutManager = origLM;

  // 좌상단 셀의 Rect 크기 확장
  const mainRect = tableGroup.getObjects().find(
    ch => ch._isTableCell && ch._cellRow === minRow && ch._cellCol === minCol
  );
  if (mainRect) {
    const pos = calcCellPosition(minRow, minCol, colWidths, rowHeights, gridSpan, rowSpan);
    mainRect.set({
      width: pos.cellW, height: pos.cellH,
      left: pos.localCx, top: pos.localCy,
    });
    mainRect._gridSpan = gridSpan;
    mainRect._rowSpan = rowSpan;
  }

  // 좌상단 Textbox 크기 확장 및 텍스트 합치기
  if (firstTextChild) {
    const mergedText = texts.join('\n');
    const marL = mainRect?._marL ?? DEFAULT_TEXT_INSET.L;
    const marR = mainRect?._marR ?? DEFAULT_TEXT_INSET.R;
    const marT = mainRect?._marT ?? DEFAULT_TEXT_INSET.T;
    const marB = mainRect?._marB ?? DEFAULT_TEXT_INSET.B;
    const pos = calcCellPosition(minRow, minCol, colWidths, rowHeights, gridSpan, rowSpan);
    const textOffX = (marL - marR) / 2;
    firstTextChild.set({
      text: mergedText,
      width: pos.cellW - marL - marR,
      left: pos.localCx + textOffX, top: pos.localCy, // 세로 중앙정렬
    });
    firstTextChild._gridSpan = gridSpan;
    firstTextChild._rowSpan = rowSpan;
  }

  const affectedRows = Array.from({ length: maxRow - minRow + 1 }, (_, i) => minRow + i);
  recalcTableLayout(tableGroup, savedPos, affectedRows);
  if (canvas) canvas.requestRenderAll();
  return true;
};

/**
 * 셀 병합 해제
 * @param {fabric.Group} tableGroup
 * @param {number} row - 병합 시작 셀의 행
 * @param {number} col - 병합 시작 셀의 열
 * @param {fabric.Canvas} canvas
 * @param {boolean} skipRender - 렌더링 스킵 (연속 작업 시)
 * @returns {boolean}
 */
export const unmergeCells = (tableGroup, row, col, canvas, skipRender = false) => {
  const mainRect = tableGroup.getObjects().find(
    ch => ch._isTableCell && ch._cellRow === row && ch._cellCol === col
  );
  if (!mainRect) return false;

  const gridSpan = mainRect._gridSpan || 1;
  const rowSpan = mainRect._rowSpan || 1;
  if (gridSpan <= 1 && rowSpan <= 1) return false;

  const savedPos = { left: tableGroup.left, top: tableGroup.top };
  const colWidths = tableGroup._tableColWidths;
  const rowHeights = tableGroup._tableRowHeights;

  // 좌상단 Rect를 원래 크기로 축소
  const pos0 = calcCellPosition(row, col, colWidths, rowHeights, 1, 1);
  mainRect.set({
    width: pos0.cellW, height: pos0.cellH,
    left: pos0.localCx, top: pos0.localCy,
  });
  mainRect._gridSpan = 1;
  mainRect._rowSpan = 1;

  // 좌상단 Textbox도 축소
  const mainText = tableGroup.getObjects().find(
    ch => ch._isTableCellText && ch._cellRow === row && ch._cellCol === col
  );
  if (mainText) {
    const marL = mainRect._marL ?? DEFAULT_TEXT_INSET.L;
    const marR = mainRect._marR ?? DEFAULT_TEXT_INSET.R;
    const textOffX = (marL - (mainRect._marR ?? DEFAULT_TEXT_INSET.R)) / 2;
    mainText.set({
      width: pos0.cellW - marL - marR,
      left: pos0.localCx + textOffX,
      top: pos0.localCy, // 세로 중앙정렬
    });
    mainText._gridSpan = 1;
    mainText._rowSpan = 1;
  }

  // 가려졌던 셀 재생성
  const borderColor = mainRect._origStroke || '#000000';
  const borderWidth = mainRect._origStrokeWidth ?? 1;
  const cellFill = mainRect._origFill ?? mainRect.fill ?? 'transparent';
  const isEditMode = tableGroup.interactive === true;
  const defaultBorders = {
    T: { color: borderColor, width: borderWidth },
    B: { color: borderColor, width: borderWidth },
    L: { color: borderColor, width: borderWidth },
    R: { color: borderColor, width: borderWidth },
  };

  // FitContentLayout 억제: add()가 자식 위치를 오염시키는 것 방지
  const origLM = tableGroup.layoutManager;
  tableGroup.layoutManager = NOOP_LAYOUT_MANAGER;

  for (let r = row; r < row + rowSpan; r++) {
    for (let c = col; c < col + gridSpan; c++) {
      if (r === row && c === col) continue;
      const pos = calcCellPosition(r, c, colWidths, rowHeights, 1, 1);
      const marL = mainRect._marL ?? DEFAULT_TEXT_INSET.L;
      const marR = mainRect._marR ?? DEFAULT_TEXT_INSET.R;
      const marT = mainRect._marT ?? DEFAULT_TEXT_INSET.T;
      const marB = mainRect._marB ?? DEFAULT_TEXT_INSET.B;

      const newRect = new fabric.Rect({
        width: pos.cellW, height: pos.cellH,
        fill: cellFill,
        opacity: mainRect.opacity ?? 1,
        stroke: null, strokeWidth: 0,
        left: pos.localCx, top: pos.localCy,
        originX: 'center', originY: 'center',
        selectable: false, evented: false,
        _cellRow: r, _cellCol: c, _isTableCell: true,
        _gridSpan: 1, _rowSpan: 1,
        _marL: marL, _marR: marR, _marT: marT, _marB: marB,
        _borders: { ...defaultBorders },
        _origStroke: borderColor, _origStrokeWidth: borderWidth,
      });

      const textOffX = (marL - marR) / 2;
      const newText = new fabric.Textbox('', {
        width: pos.cellW - marL - marR,
        left: pos.localCx + textOffX, top: pos.localCy,
        originX: 'center', originY: 'center',
        fontFamily: mainText?.fontFamily || 'Noto Sans KR',
        fontSize: mainText?.fontSize || 14,
        fill: mainText?.fill || '#000000',
        splitByGrapheme: true, wordWrap: true,
        selectable: true, evented: true,
        hasBorders: !isEditMode, hasControls: !isEditMode,
        lockMovementX: isEditMode, lockMovementY: isEditMode,
        _cellRow: r, _cellCol: c, _isTableCellText: true,
        _gridSpan: 1, _rowSpan: 1,
      });

      tableGroup.add(newRect);
      tableGroup.add(newText);
    }
  }

  tableGroup.layoutManager = origLM;
  const affectedRows = Array.from({ length: rowSpan }, (_, i) => row + i);
  recalcTableLayout(tableGroup, savedPos, affectedRows);
  if (!skipRender && canvas) canvas.requestRenderAll();
  return true;
};

/**
 * 주어진 셀이 병합된 셀인지 확인
 * @param {fabric.Group} tableGroup
 * @param {number} row
 * @param {number} col
 * @returns {{isMerged: boolean, gridSpan: number, rowSpan: number}}
 */
export const getCellMergeInfo = (tableGroup, row, col) => {
  const rect = tableGroup.getObjects().find(
    ch => ch._isTableCell && ch._cellRow === row && ch._cellCol === col
  );
  if (!rect) return { isMerged: false, gridSpan: 1, rowSpan: 1 };
  const gs = rect._gridSpan || 1;
  const rs = rect._rowSpan || 1;
  return { isMerged: gs > 1 || rs > 1, gridSpan: gs, rowSpan: rs };
};
